import {
  CheckBoxSwitcher,
  FormSelect,
  Hint,
  Popover,
  PrimaryButton,
  TimePicker,
  toast,
} from '@systemeio/ui'
import { BadRequest } from '@systemeio/util'
import { updateFunnelDeadlineSettings } from 'modules/funnels/funnel/api/funnel-api'
import DeadlineSettingsExpirationInput from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-expiration-input'
import DeadlineSettingsRadioGroup from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-radio-group'
import DeadlineSettingsTabSkeleton from 'modules/funnels/funnel/components/deadline-settings-tab/deadline-settings-tab-skeleton'
import MobileSettingsHeader from 'modules/funnels/funnel/components/mobile-settings-header'
import { FunnelStepSettingTabsEnum } from 'modules/funnels/funnel/enums/funnel-step-setting-tabs-enum'
import { useFunnelDeadlineSettings } from 'modules/funnels/funnel/hooks/use-funnel-deadline-settings'
import { useFunnelStepsList } from 'modules/funnels/funnel/hooks/use-funnel-steps-list'
import {
  DeadlineSettingsEditInterface,
  DeadlineSettingsErrorsInterface,
} from 'modules/funnels/funnel/types/deadline-settings-interface'
import { FunnelStepInterface } from 'modules/funnels/funnel/types/funnel-step-interface'
import {
  getAvailableFunnelStepsData,
  getFunnelStepsToRedirectData,
} from 'modules/funnels/funnel/utils/deadline-settings-tab-utils'
import { FunnelId } from 'modules/funnels/types/funnel-interface'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { fetchWithFieldErrors } from 'shared/utils/fetch-with-errors'
import { getSecondsWithTimeZone, getSecondsWithoutTimeZone } from 'shared/utils/time-utils'
import DeadlineSettingsItemLayout from './deadline-settings-item-layout'
import { QuestionMarkIcon, SaveIcon, WarningIcon } from '@systemeio/ui/icons'

const defaultValues: DeadlineSettingsEditInterface = {
  enabled: false,
  deadlinePageExpirationInDays: undefined,
  expirationFunnelStep: undefined,
  deadlineTime: null,
  deadlineFunnelSteps: [],
}

const defaultErrors: DeadlineSettingsErrorsInterface = {
  fields: {
    enabled: '',
    deadlinePageExpirationInDays: '',
    expirationFunnelStep: '',
    deadlineTime: '',
    deadlineFunnelSteps: '',
  },
}

interface DeadlineSettingsTabProps {
  funnelId: FunnelId
  funnelStep: FunnelStepInterface | null
}

function DeadlineSettingsTab({ funnelId }: DeadlineSettingsTabProps) {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  const { funnelStepsList } = useFunnelStepsList(funnelId)
  const { deadlineSettings, mutate } = useFunnelDeadlineSettings(funnelId)
  const [tempState, setTempState] = useState(defaultValues)
  const [errors, setErrors] = useState(defaultErrors)
  const [isExpirationTime, setIsExpirationTime] = useState(false)
  const [deadlineSeconds, setDeadlineSeconds] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const funnelStepsToRedirectData = getFunnelStepsToRedirectData(funnelStepsList)

  const availableFunnelStepsData = getAvailableFunnelStepsData(funnelStepsList)

  const handleConfirm = async () => {
    try {
      setIsLoading(true)
      await fetchWithFieldErrors(async () => {
        const newData = {
          ...tempState,
          deadlineTime: getSecondsWithTimeZone(deadlineSeconds, user?.timezone),
        }
        const response = await updateFunnelDeadlineSettings(funnelId, newData)
        await mutate(data => {
          if (data) {
            return response.data
          }
        }, false)
      }, setErrors)
      setIsLoading(false)
      toast.success(t('global.changes_saved'))
    } catch (error) {
      setIsLoading(false)
      if (error instanceof BadRequest) {
        if (error.errors.common) {
          const commonErrors = error.errors.common
          setErrors(prev => {
            return {
              ...prev,
              common: commonErrors,
            }
          })
        }
      } else {
        toast.error(t('global.error'))
      }
    }
  }

  useEffect(() => {
    if (deadlineSettings) {
      setTempState(prev => {
        return {
          ...prev,
          deadlineFunnelSteps: deadlineSettings.deadlineFunnelSteps.map(
            funnelStep => funnelStep.id,
          ),
          deadlinePageExpirationInDays: deadlineSettings.deadlinePageExpirationInDays ?? 0,
          deadlineTime: deadlineSettings.deadlineTime,
          enabled: deadlineSettings.enabled,
          expirationFunnelStep: deadlineSettings.expirationFunnelStep?.id,
        }
      })
      if (deadlineSettings.deadlineTime) {
        setIsExpirationTime(true)
        setDeadlineSeconds(getSecondsWithoutTimeZone(deadlineSettings.deadlineTime, user?.timezone))
      }
    }
  }, [deadlineSettings, user?.timezone])

  return (
    <div className="mb-7 flex flex-col gap-10 px-[15px] lg:px-[15px]">
      <MobileSettingsHeader type={FunnelStepSettingTabsEnum.deadlineSettings} />
      {deadlineSettings && funnelStepsList ? (
        <>
          <DeadlineSettingsItemLayout>
            <div className="flex items-center justify-between ">
              <CheckBoxSwitcher
                label={
                  <Hint label={t('dashboard.funnel.deadline_settings_description')}>
                    {t('dashboard.funnel.deadline_settings_enable')}
                  </Hint>
                }
                labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary leading-6'}
                selected={tempState.enabled}
                onChange={value =>
                  setTempState(prev => {
                    return { ...prev, enabled: value }
                  })
                }
                disabled={!deadlineSettings}
              />
              <PrimaryButton
                disabled={false}
                width="large"
                onClick={handleConfirm}
                isFetching={isLoading}
                type="submit"
                className={'hidden lg:flex'}
              >
                <SaveIcon />
                <span className={'hidden lg:flex'}>
                  {t('dashboard.funnel.deadline_settings_save')}
                </span>
              </PrimaryButton>
            </div>
          </DeadlineSettingsItemLayout>
          <DeadlineSettingsItemLayout>
            <div className="flex flex-col gap-10 lg:flex-row">
              <DeadlineSettingsExpirationInput
                labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
                onChange={value =>
                  setTempState(prev => {
                    return { ...prev, deadlinePageExpirationInDays: value }
                  })
                }
                value={tempState.deadlinePageExpirationInDays || 0}
                isFetching={false}
                label={t('dashboard.funnel.deadline_settings_deadline_expiration_title')}
                required
                wrapperClassName={'flex-[40_1_0%]'}
                error={errors.fields.deadlinePageExpirationInDays}
              />
              <FormSelect
                label={t('dashboard.funnel.deadline_settings_expiration_funnel_step_title')}
                labelClassName={'lg:text-[15px] font-medium text-sm text-text-primary'}
                value={tempState.expirationFunnelStep}
                data={funnelStepsToRedirectData}
                onChange={value =>
                  setTempState(prev => {
                    return {
                      ...prev,
                      expirationFunnelStep: value,
                    }
                  })
                }
                required
                containerClassName={'flex-[60_1_0%]'}
                error={errors.fields.expirationFunnelStep}
              />
            </div>
          </DeadlineSettingsItemLayout>
          <DeadlineSettingsItemLayout>
            <div className="flex flex-col gap-5">
              <CheckBoxSwitcher
                label={
                  <div
                    className={
                      'text-text-primary flex items-center gap-1.5 text-sm font-medium leading-6 lg:text-[15px]'
                    }
                  >
                    {t('dashboard.funnel.deadline_settings_expiration_time_title')}
                    <Popover
                      label={t('dashboard.funnel.deadline_settings_expiration_time_tooltip')}
                    >
                      {show => (
                        <QuestionMarkIcon
                          className={`${
                            show ? 'fill-brand-primary' : 'fill-fill-primary'
                          } group-focus-visible:fill-brand-primary h-[16px] w-[16px]`}
                        />
                      )}
                    </Popover>
                  </div>
                }
                selected={isExpirationTime}
                onChange={value => {
                  if (!value) {
                    setDeadlineSeconds(null)
                  }
                  setIsExpirationTime(value)
                }}
              />
              {isExpirationTime && (
                <TimePicker
                  seconds={deadlineSeconds}
                  onChangeSeconds={setDeadlineSeconds}
                  userDateFormat={user?.dateFormat}
                  userTimeFormat={user?.timeFormat}
                />
              )}
            </div>
          </DeadlineSettingsItemLayout>
          <div className="flex flex-col gap-5">
            <div className={'flex items-start gap-1.5'}>
              <span className="text-text-primary text-sm font-medium lg:text-[15px]">
                {t('dashboard.funnel.deadline_settings_available_funnel_steps_title')}
              </span>
              <Popover
                label={t('dashboard.funnel.deadline_settings_available_funnel_steps_tooltip')}
              >
                {() => <WarningIcon className="h-[18px] w-[18px]" />}
              </Popover>
            </div>
            <DeadlineSettingsRadioGroup
              options={availableFunnelStepsData}
              selected={tempState.deadlineFunnelSteps}
              onChange={value => {
                const isSelected = tempState.deadlineFunnelSteps.includes(value)
                if (isSelected) {
                  setTempState(prev => ({
                    ...prev,
                    deadlineFunnelSteps: prev.deadlineFunnelSteps.filter(
                      funnelStepId => funnelStepId !== value,
                    ),
                  }))
                } else {
                  setTempState(prev => ({
                    ...prev,
                    deadlineFunnelSteps: [...prev.deadlineFunnelSteps, value],
                  }))
                }
              }}
              error={errors.fields.deadlineFunnelSteps}
            />
          </div>
          <PrimaryButton
            disabled={false}
            width="small"
            onClick={handleConfirm}
            isFetching={isLoading}
            type="submit"
            className={'max-w-fit lg:hidden'}
          >
            <SaveIcon />
            <span className={'flex'}>{t('dashboard.funnel.deadline_settings_save')}</span>
          </PrimaryButton>
        </>
      ) : (
        <DeadlineSettingsTabSkeleton />
      )}
      {errors.common && (
        <>
          {errors.common.map((error, index) => (
            <p className="text-danger px-6 text-sm" key={index}>
              {error}
            </p>
          ))}
        </>
      )}
    </div>
  )
}

export default DeadlineSettingsTab
